/**
 * FAQ Service
 * Handles API calls related to FAQs
 */
import api from '@shared/services/api';
import { ApiResponse } from '@shared/types/api';
import { Faq, FaqFormData, FaqResponse } from '../types/faqs.types';

/**
 * Service for handling FAQ related API calls
 */
export class FaqService {
  private static BASE_URL = '/faqs';

  /**
   * Fetch all FAQs with optional filtering
   * @returns Promise with FAQs data and categories
   */
  static async getAllFaqs(): Promise<FaqResponse> {
    const response = await api.get<
      ApiResponse<{ data: Faq[]; categories: string[] }>
    >(this.BASE_URL);
    return response.data.data;
  }

  /**
   * Get a single FAQ by ID
   * @param id FAQ ID
   * @returns Promise with FAQ data
   */
  static async getFaqById(id: string): Promise<Faq> {
    const response = await api.get<ApiResponse<Faq>>(`${this.BASE_URL}/${id}`);
    return response.data.data;
  }

  /**
   * Create a new FAQ
   * @param data FAQ data
   * @returns Promise with created FAQ
   */
  static async createFaq(data: FaqFormData): Promise<Faq> {
    const response = await api.post<ApiResponse<Faq>>(this.BASE_URL, data);
    return response.data.data;
  }

  /**
   * Update an existing FAQ
   * @param id FAQ ID
   * @param data Updated FAQ data
   * @returns Promise with updated FAQ
   */
  static async updateFaq(id: string, data: Partial<FaqFormData>): Promise<Faq> {
    const response = await api.put<ApiResponse<Faq>>(
      `${this.BASE_URL}/${id}`,
      data
    );
    return response.data.data;
  }

  /**
   * Delete a FAQ
   * @param id FAQ ID
   * @returns Promise with success message
   */
  static async deleteFaq(id: string): Promise<{ message: string }> {
    const response = await api.delete<{ message: string }>(
      `${this.BASE_URL}/${id}`
    );
    return response.data;
  }
}
